import { type LocaleTable } from "@/lib/locale";
import { PROVINCE_OPTIONS, PROVINCE_ALIASES, REMOTE_SLUG, formatLocation } from "@/lib/provinces";

// Re-exported, not redefined: the type derives from `routing.locales` so a new
// locale is a compiler event everywhere. This path is kept because callers
// already import it from here (see the note in `lib/locale.ts`).
import type { Locale } from "@/lib/locale";
export type { Locale };

export interface Suggestion {
  id: string;
  // `LocaleTable`, not a total Record: a locale with no translation falls back
  // to English, which is the house rule, rather than forcing every suggestion in
  // this file to be answered the day a locale is routed.
  label: LocaleTable<string>;
  aliases: string[];
  /** Words that MEAN this suggestion without naming it ("merge" for "M&A"),
   *  from the search synonym rules. Weaker than `aliases`: matched only from 3
   *  typed characters and ranked after every name and alias match. */
  synonyms?: string[];
  type: "category" | "location" | "combined" | "specialty";
  href: string;
}

// ── Group headers per locale ──────────────────────────────────────────────────

export const GROUP_LABELS: Record<Suggestion["type"], LocaleTable<string>> = {
  category: { en: "Categories", vi: "Danh mục", fr: "Catégories", "zh-Hans": "类别", "zh-Hant": "類別" },
  specialty: { en: "Services", vi: "Dịch vụ", fr: "Services", "zh-Hans": "服务", "zh-Hant": "服務" },
  location: { en: "Locations", vi: "Địa điểm", fr: "Emplacements", "zh-Hans": "地点", "zh-Hant": "地點" },
  combined: { en: "Suggestions", vi: "Gợi ý", fr: "Suggestions", "zh-Hans": "推荐", "zh-Hant": "推薦" },
};

// ── Suggestion entries ────────────────────────────────────────────────────────

const CATEGORY_SUGGESTIONS: Suggestion[] = [
  // ── Categories ──────────────────────────────────────────────────────────────
  {
    id: "cat-legal",
    label: { en: "Legal Counsel", vi: "Tư vấn Pháp lý", fr: "Conseil Juridique", "zh-Hans": "法律咨询", "zh-Hant": "法律諮詢" },
    aliases: ["lawyer", "lawyers", "attorney", "avocat", "luật sư", "pháp lý", "legal",
      "律师", "律师事务所", "法律", "法律咨询", "法务", "律師", "律師事務所", "法律諮詢", "法務"],
    type: "category",
    href: "/search?category=Legal",
  },
  {
    id: "cat-finance",
    label: { en: "Finance & Accounting", vi: "Tài chính & Kế toán", fr: "Finance & Comptabilité", "zh-Hans": "财务与会计", "zh-Hant": "財務與會計" },
    aliases: ["accountant", "accounting", "tax", "cpa", "kế toán", "thuế", "comptable", "fiscalité", "finance",
      "会计", "会计师", "税务", "报税", "记账", "财务", "會計", "會計師", "稅務", "報稅", "記賬", "財務"],
    type: "category",
    href: "/search?category=Finance",
  },
  {
    id: "cat-property",
    label: { en: "Real Estate", vi: "Bất động sản", fr: "Immobilier", "zh-Hans": "房地产", "zh-Hant": "房地產" },
    aliases: ["property", "real estate", "housing", "apartment", "nhà đất", "căn hộ", "immobilier",
      "房地产", "房产", "租房", "买房", "公寓", "房地產", "房產", "買房"],
    type: "category",
    href: "/search?category=Property",
  },
  {
    id: "cat-business",
    label: { en: "Business Setup", vi: "Thành lập Doanh nghiệp", fr: "Création d'entreprise", "zh-Hans": "公司设立", "zh-Hant": "公司設立" },
    aliases: ["company", "incorporation", "registration", "doanh nghiệp", "công ty", "entreprise", "business",
      "公司设立", "注册公司", "开公司", "企业登记", "营业执照", "公司設立", "註冊公司", "開公司", "企業登記", "營業執照"],
    type: "category",
    href: "/search?category=Business",
  },
  {
    id: "cat-visa",
    label: { en: "Visa Services", vi: "Dịch vụ Visa", fr: "Services Visa", "zh-Hans": "签证服务", "zh-Hant": "簽證服務" },
    aliases: ["visa", "immigration", "work permit", "nhập cư", "giấy phép", "permis",
      "签证", "移民", "工作许可", "工作许可证", "居留卡", "临时居留卡", "簽證", "工作許可", "工作許可證", "臨時居留卡"],
    type: "category",
    href: "/search?category=Visa",
  },
  {
    id: "cat-audit",
    label: { en: "Audit & Compliance", vi: "Kiểm toán & Tuân thủ", fr: "Audit & Conformité", "zh-Hans": "审计与合规", "zh-Hant": "審計與合規" },
    aliases: ["audit", "compliance", "kiểm toán", "conformité", "审计", "合规", "審計", "合規"],
    type: "category",
    href: "/search?category=Audit",
  },
  {
    id: "cat-notary",
    label: { en: "Notary Services", vi: "Dịch vụ Công chứng", fr: "Services Notariaux", "zh-Hans": "公证服务", "zh-Hant": "公證服務" },
    aliases: ["notary", "legalization", "công chứng", "notaire",
      "公证", "领事认证", "海牙认证", "公證", "領事認證", "海牙認證"],
    type: "category",
    href: "/search?category=Notary",
  },
  {
    id: "cat-translation",
    label: { en: "Translation", vi: "Dịch thuật", fr: "Traduction", "zh-Hans": "翻译", "zh-Hant": "翻譯" },
    aliases: ["translation", "interpreter", "dịch thuật", "phiên dịch", "traduction", "interprète",
      "翻译", "口译", "笔译", "翻譯", "口譯", "筆譯"],
    type: "category",
    href: "/search?category=Translation",
  },
  {
    id: "cat-insurance",
    label: { en: "Insurance", vi: "Bảo hiểm", fr: "Assurance", "zh-Hans": "保险", "zh-Hant": "保險" },
    aliases: ["insurance", "health insurance", "life insurance", "bảo hiểm", "bảo hiểm y tế", "assurance", "assurance santé",
      "保险", "医疗保险", "健康保险", "人寿保险", "保險", "醫療保險", "健康保險", "人壽保險"],
    type: "category",
    href: "/search?category=Insurance",
  },
  {
    id: "cat-relocation",
    label: { en: "Relocation", vi: "Hỗ trợ định cư", fr: "Relocation", "zh-Hans": "搬迁服务", "zh-Hant": "搬遷服務" },
    aliases: ["relocation", "moving", "movers", "settling in", "hỗ trợ định cư", "định cư", "chuyển nhà", "déménagement", "installation",
      "搬迁", "搬家", "国际搬家", "搬遷", "國際搬家"],
    type: "category",
    href: "/search?category=Relocation",
  },
  {
    id: "cat-permit",
    label: { en: "Permits & Licences", vi: "Giấy phép", fr: "Permis & licences", "zh-Hans": "许可与执照", "zh-Hant": "許可與執照" },
    // Both spellings on purpose: the house style is British ("Licences,
    // Rules & Safety") and users type either — and unlike the autocomplete
    // comparators, which prefix-match, licence/license differ BEFORE the
    // suffix, so neither form finds the other on its own.
    aliases: [
      "permit", "permits", "licence", "licences", "license", "licenses",
      "licensing", "work permit", "business licence", "business license",
      "driving licence", "driving license", "construction permit",
      "giấy phép", "giấy phép lao động", "cấp phép", "bằng lái",
      "permis", "licence de travail", "autorisation",
      "许可", "许可证", "执照", "牌照", "工作许可证", "驾照", "驾驶证", "建筑许可",
      "許可", "許可證", "執照", "工作許可證", "駕照", "駕駛證", "建築許可",
    ],
    type: "category",
    href: "/search?category=Permit",
  },
  {
    id: "cat-office",
    label: { en: "Office & Workspace", vi: "Văn phòng làm việc", fr: "Bureaux & espaces de travail", "zh-Hans": "办公室与办公空间", "zh-Hant": "辦公室與辦公空間" },
    // ⚠️ No bare "văn phòng": in Vietnamese it also names a law or notary
    // PRACTICE ("văn phòng luật sư", "văn phòng công chứng"), so on its own it
    // would pull readers looking for a lawyer onto this shelf. Every VI alias
    // here is a phrase that can only mean premises.
    aliases: [
      "office", "offices", "office space", "office rental", "serviced office",
      "virtual office", "workspace", "coworking", "co-working", "shared office",
      "văn phòng làm việc", "văn phòng cho thuê", "thuê văn phòng", "văn phòng ảo",
      "văn phòng trọn gói", "không gian làm việc chung",
      "bureau", "bureaux", "espace de travail", "bureau virtuel",
      "办公室", "写字楼", "办公空间", "联合办公", "共享办公", "虚拟办公室", "服务式办公室",
      "辦公室", "寫字樓", "辦公空間", "聯合辦公", "共享辦公", "虛擬辦公室", "服務式辦公室",
    ],
    type: "category",
    href: "/search?category=Office",
  },
  {
    id: "cat-hr",
    label: { en: "HR & Payroll", vi: "Nhân sự & Tiền lương", fr: "RH & paie", "zh-Hans": "人力资源与薪资", "zh-Hant": "人力資源與薪資" },
    // ⚠️ No bare "lao động" (labour): "giấy phép lao động" is a WORK PERMIT,
    // which the Visa and Permit shelves answer. Every VI alias here is a phrase that can only
    // mean employing people. "hr" is under three characters, so the typo
    // suggester and the affiliate matcher skip it; it only serves prefix typing.
    aliases: [
      "hr", "human resources", "payroll", "payroll outsourcing", "hr outsourcing",
      "social insurance", "labour contract", "labor contract", "employment contract",
      "employer of record", "recruitment", "recruiter", "headhunter", "headhunting", "staffing",
      "nhân sự", "tiền lương", "tính lương", "bảng lương", "bảo hiểm xã hội",
      "hợp đồng lao động", "tuyển dụng", "thuê ngoài nhân sự", "săn đầu người",
      // Not a bare "paie": four letters, so the typo suggester would offer this
      // shelf for "paid" or "pair". The label "RH & paie" still matches it.
      "ressources humaines", "recrutement",
      // Chinese in both scripts (matching is literal). No bare 劳动 (labour),
      // for the same work-permit reason as the VI note above.
      "人力资源", "人事", "薪资", "工资", "代发工资", "社保", "社会保险", "劳动合同", "招聘", "猎头",
      "人力資源", "薪資", "工資", "代發工資", "社會保險", "勞動合同", "獵頭",
    ],
    type: "category",
    href: "/search?category=HR",
  },

];

// ── Locations ───────────────────────────────────────────────────────────────
//
// Derived from the province catalogue (src/lib/provinces.ts) rather than
// hardcoded, so the slug in each href always matches the DB-stored `location`
// value and the count-map keys. The previous hardcoded list used pre-2025
// slugs (hcmc/hanoi/danang/…) which silently broke after the province reform:
// every location suggestion scored 0 results (so was filtered out) and linked
// to a slug no professional row carried.

// Famous wards / former cities people still search by, mapped to their current
// parent province slug. Kept here (not in the generated provinces.ts) because
// they're search affordances, not catalogue entries — Hội An folded into Đà
// Nẵng and Nha Trang into Khánh Hòa under the 2025 reform.
const WARD_ALIASES: Record<string, string[]> = {
  "da-nang": ["hoi an", "hội an", "hoian", "会安", "會安"],
  "khanh-hoa": ["nha trang", "nhatrang", "芽庄", "芽莊"],
};

// Chinese names people type for the cities that have one (Simplified, then
// Traditional where it differs: matching is literal). The zh LABEL already
// carries the full name for these six (`provinceLabel`); these catch the short
// forms and Saigon, and let the English UI answer a Chinese query too.
const ZH_CITY_ALIASES: Record<string, string[]> = {
  "ho-chi-minh": ["胡志明", "胡志明市", "西贡", "西貢"],
  "ha-noi": ["河内", "河內"],
  "da-nang": ["岘港", "峴港"],
  "hai-phong": ["海防"],
  "can-tho": ["芹苴"],
  hue: ["顺化", "順化"],
};

// Invert PROVINCE_ALIASES (alias → slug) into slug → alias[] so each province
// suggestion carries its colloquial / historical names (Saigon, HCMC, …).
const ALIASES_BY_SLUG: Record<string, string[]> = {};
for (const [alias, slug] of Object.entries(PROVINCE_ALIASES)) {
  (ALIASES_BY_SLUG[slug] ??= []).push(alias);
}

const LOCATION_SUGGESTIONS: Suggestion[] = [
  ...PROVINCE_OPTIONS.map((p) => ({
    id: `loc-${p.slug}`,
    // Mirrors formatLocation: EN/FR use the ASCII name, VI the diacritic name,
    // zh the Chinese name where one is set and the ASCII name otherwise.
    label: {
      en: p.nameEn,
      vi: p.name,
      fr: p.nameEn,
      "zh-Hans": formatLocation(p.slug, "zh-Hans"),
      "zh-Hant": formatLocation(p.slug, "zh-Hant"),
    },
    aliases: [
      p.slug,
      ...(ALIASES_BY_SLUG[p.slug] ?? []),
      ...(WARD_ALIASES[p.slug] ?? []),
      ...(ZH_CITY_ALIASES[p.slug] ?? []),
    ],
    type: "location" as const,
    href: `/search?location=${p.slug}`,
  })),
  // The `remote` sentinel isn't in PROVINCE_OPTIONS, so it needs its own
  // suggestion or the hero never offers it — a VI user typing "Từ xa" (the
  // label the UI shows) hits "No professionals found" despite remote pros
  // existing. Labels come from formatLocation so they match every other
  // surface; the count key is `remote`, which get_suggestion_counts emits.
  {
    id: `loc-${REMOTE_SLUG}`,
    label: {
      en: formatLocation(REMOTE_SLUG, "en"),
      vi: formatLocation(REMOTE_SLUG, "vi"),
      fr: formatLocation(REMOTE_SLUG, "fr"),
      "zh-Hans": formatLocation(REMOTE_SLUG, "zh-Hans"),
      "zh-Hant": formatLocation(REMOTE_SLUG, "zh-Hant"),
    },
    aliases: [REMOTE_SLUG, "online", "tu xa", "remote / online", "远程", "线上", "遠程", "線上"],
    type: "location" as const,
    href: `/search?location=${REMOTE_SLUG}`,
  },
];

export const SUGGESTIONS: Suggestion[] = [...CATEGORY_SUGGESTIONS, ...LOCATION_SUGGESTIONS];
